import { h } from 'preact';
import { useEffect, useState, useCallback, useMemo } from 'preact/hooks';
import { toCamelCase } from 'utils/general';

const FABRIC_WEIGHT = 'fabric weight';
const COLOR = 'Color';

const FullWidthRadio = ({
  product,
  currentVariant,
  handleVariantChange,
  checkedOptions,
  setCheckedOptions,
  filteredVariants,
  template,
}) => {
  const radioOptionIndex = useMemo(
    () => product.options?.findIndex((option) => option.name.toLowerCase().includes(FABRIC_WEIGHT)),
    [product.options],
  );

  const colorOptionIndex = useMemo(
    () => currentVariant.selectedOptions.findIndex((opt) => opt.name === COLOR),
    [currentVariant.selectedOptions],
  );

  if (radioOptionIndex < 0) return null;

  const colorOption = currentVariant.selectedOptions.find((opt) => opt.name === COLOR)?.value;

  const basicRadioVariants = useMemo(
    () =>
      product.variants.filter(
        (variant, index, self) =>
          index ===
          self.findIndex(
            (otherVariant) =>
              otherVariant.selectedOptions[radioOptionIndex].value === variant.selectedOptions[radioOptionIndex].value,
          ),
      ),
    [product.variants, radioOptionIndex],
  );

  const colorVariants = useMemo(
    () =>
      product.variants.filter((variant) =>
        variant.selectedOptions.some((opt) => opt.name === COLOR && opt.value === colorOption),
      ),
    [product.variants, colorOption],
  );

  const fabricWeights = useMemo(
    () => [
      ...new Set(
        filteredVariants
          .map(
            (item) =>
              item.selectedOptions[colorOptionIndex]?.value === colorOption &&
              item.selectedOptions[radioOptionIndex]?.value,
          )
          .filter(Boolean),
      ),
    ],
    [filteredVariants, colorOptionIndex, colorOption, radioOptionIndex],
  );

  const [radioVariants, setRadioVariants] = useState(basicRadioVariants);

  useEffect(() => {
    if (template !== 'pdp') return;

    const updatedRadioVariants = basicRadioVariants
      .filter((item) => fabricWeights.includes(item.selectedOptions[radioOptionIndex]?.value))
      .sort((a, b) => {
        const weightOrder = ['Crusher', 'Crusher-Lite'];
        const weightA = a?.selectedOptions?.find((opt) => opt?.name === 'Fabric Weight')?.value;
        const weightB = b?.selectedOptions?.find((opt) => opt?.name === 'Fabric Weight')?.value;

        return weightOrder.indexOf(weightA) - weightOrder.indexOf(weightB);
      });

    if (JSON.stringify(updatedRadioVariants) !== JSON.stringify(radioVariants)) {
      setRadioVariants(updatedRadioVariants);
    }
  }, [filteredVariants, currentVariant, template, basicRadioVariants, fabricWeights, radioOptionIndex, radioVariants]);

  useEffect(() => {
    if (fabricWeights.length !== 1) return;
    setCheckedOptions((prev) => ({ ...prev, weight: true }));
  }, [radioVariants, fabricWeights, filteredVariants, colorOption]);

  const handleVariantSelection = useCallback(
    (e) => {
      handleVariantChange(e);
      setCheckedOptions((prev) => ({ ...prev, weight: true }));
    },
    [handleVariantChange, setCheckedOptions],
  );

  if (radioVariants.length < 1) return null;

  return (
    <div>
      {radioVariants.map((variant) => {
        const selectedRadioOption = currentVariant?.selectedOptions.find(
          (option) => option.name.toLowerCase() === FABRIC_WEIGHT,
        );
        const radioIndex = variant.selectedOptions.findIndex((option) => option.name.toLowerCase() === FABRIC_WEIGHT);
        const radioOption = variant.selectedOptions[radioIndex].value;
        const description = window.TMW.Strings.product[`${toCamelCase(radioOption)}Description`] || '';

        const checked =
          (checkedOptions.weight && selectedRadioOption?.value === radioOption) || radioVariants.length === 1;
        const optionExists = colorVariants.some(
          (colorVariant) => colorVariant.selectedOptions[radioIndex].value === radioOption,
        );

        return (
          <label
            key={variant.id}
            className={`variant-radio mb-[10px] flex max-w-[447px] cursor-pointer items-center gap-6 rounded
                       border border-grey-5 bg-grey-6 p-12 last:mb-0 md:py-15 ${optionExists ? '' : 'opacity-20'}`}
          >
            <input
              checked={checked}
              id={variant.id.split('/ProductVariant/')[1]}
              onChange={handleVariantSelection}
              type="radio"
              value={radioOption}
              name={`${product.idClean}-fabric-weight`}
              data-option-name="Fabric Weight"
              className="checked:bg-smokey-blue hover:text-smokey-blue focus:!bg-smokey-blue focus:ring-smokey-blue
                         cursor-pointer bg-transparent focus:ring-0 focus:ring-offset-0"
            />
            <span className="body-12-med md:body-14-med text-smokey-blue hover:underline">{radioOption}</span>
            <span className="body-12-light md:body-14-light">{description}</span>
          </label>
        );
      })}
    </div>
  );
};

export default FullWidthRadio;
